import classNames from "classnames";
import { CSSProperties, ReactNode, useCallback } from "react";
import {
    useDefaultLayout,
    useGroupRef,
    type Layout,
    type LayoutChangedMeta,
} from "react-resizable-panels";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import previewStyles from "@components/invoices/NativeInvoiceDrawer/InvoiceDocumentPreview.module.scss";
import invoiceStyles from "@components/invoices/NativeInvoiceDrawer/NativeInvoiceDrawer.module.scss";
import sidePanelStyles from "@components/invoices/NativeInvoiceDrawer/SidePanel.module.scss";
import { usePreviewViewportHeight } from "@components/invoices/NativeInvoiceDrawer/usePreviewViewportHeight";

interface Props {
    left: ReactNode;
    right: ReactNode;
    /** Command island floated across the full width of the drawer body. */
    footer?: ReactNode;
}

const SIDE_PANEL_ID = "report-settings-side-panel";
const PREVIEW_PANEL_ID = "report-settings-preview-panel";
const PANEL_GROUP_ID = "report-settings-panel-group";
const EVEN_SPLIT = 50;
const EVEN_SPLIT_SNAP_TOLERANCE = 1.5;
const FLOW_GROUP_STYLE: CSSProperties = {
    alignSelf: "flex-start",
    height: "auto",
    minHeight: "var(--invoice-preview-height, 100%)",
    minWidth: 0,
    overflow: "visible",
};
const FLOW_PANEL_STYLE: CSSProperties = {
    minWidth: 0,
    overflowX: "clip",
    overflowY: "visible",
};

const isDegenerateLayout = (layout: Layout): boolean =>
    Object.values(layout).some((size) => !Number.isFinite(size) || size <= 0);

/** Two-column drawer body: editor controls on the left, live preview on the right. */
export const PreviewSplit = ({ left, right, footer }: Props) => {
    const { defaultLayout, onLayoutChanged } = useDefaultLayout({
        groupId: PANEL_GROUP_ID,
        storage: localStorage,
    });
    const panelGroupRef = useGroupRef();
    const bodyRef = usePreviewViewportHeight();

    const restoredLayout =
        defaultLayout && !isDegenerateLayout(defaultLayout)
            ? defaultLayout
            : undefined;

    const handleLayoutSnap = useCallback(
        (layout: Layout) => {
            const sidePanelSize = layout[SIDE_PANEL_ID];
            const shouldSnapToEvenSplit =
                sidePanelSize !== undefined &&
                sidePanelSize !== EVEN_SPLIT &&
                Math.abs(sidePanelSize - EVEN_SPLIT) <=
                    EVEN_SPLIT_SNAP_TOLERANCE;
            if (shouldSnapToEvenSplit) {
                panelGroupRef.current?.setLayout({
                    [SIDE_PANEL_ID]: EVEN_SPLIT,
                    [PREVIEW_PANEL_ID]: EVEN_SPLIT,
                });
            }
        },
        [panelGroupRef],
    );

    const handleLayoutChanged = useCallback(
        (layout: Layout, meta: LayoutChangedMeta) => {
            if (isDegenerateLayout(layout)) {
                return;
            }
            onLayoutChanged(layout, meta);
        },
        [onLayoutChanged],
    );

    return (
        <div className={invoiceStyles.workspace}>
            <div ref={bodyRef} className={invoiceStyles.body}>
                <ResizablePanelGroup
                    style={FLOW_GROUP_STYLE}
                    orientation="horizontal"
                    groupRef={panelGroupRef}
                    defaultLayout={restoredLayout}
                    onLayoutChange={handleLayoutSnap}
                    onLayoutChanged={handleLayoutChanged}
                >
                    <ResizablePanel
                        defaultSize="50%"
                        minSize="40%"
                        maxSize="60%"
                        id={SIDE_PANEL_ID}
                        style={FLOW_PANEL_STYLE}
                    >
                        <aside className={invoiceStyles.sidePanel}>
                            <div className={sidePanelStyles.panelContent}>
                                {left}
                            </div>
                        </aside>
                    </ResizablePanel>
                    <ResizableHandle
                        className={invoiceStyles.invisibleResizeHandle}
                    />
                    <ResizablePanel
                        defaultSize="50%"
                        minSize="40%"
                        maxSize="60%"
                        id={PREVIEW_PANEL_ID}
                        style={FLOW_PANEL_STYLE}
                    >
                        <div
                            className={classNames(
                                invoiceStyles.previewPane,
                                invoiceStyles.previewPaneFloating,
                            )}
                        >
                            <div
                                className={classNames(
                                    previewStyles.paperScaleViewport,
                                    previewStyles.paperScaleViewportFitHeight,
                                )}
                            >
                                {right}
                            </div>
                        </div>
                    </ResizablePanel>
                </ResizablePanelGroup>
            </div>
            {footer}
        </div>
    );
};
